/**
 * Tutor documents detail tab.
 * Criminal-record file + decision, 360 certification, and contract.
 */
import * as React from 'react';
import {
  FileCheck,
  GraduationCap,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  Eye,
  Download,
} from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { useQuery } from '@tanstack/react-query';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import { ConfirmModal } from '@/components/common/modals/ConfirmModal';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { Button } from '@/components/forms/Button';
import { useGetTutorCriminalRecord, useUpdateCriminalRecordDecision } from '@/hooks/useUsers';
import { useAuthStore } from '@/stores/authStore';
import { usePageActions } from '@/hooks/usePageActions';
import type { TutorRecord } from '@/types/remote/tutor.types';
import {
  formatDate,
  formatLearning360DateTime,
  formatLearning360TimeSpent,
  formatTutorDisplayName,
  isTutorTutoringInProgress,
} from '@/components/tutors/remote/tutor-table-utils';
import { ActivePairsBlockModal } from '@/components/common/modals/ActivePairsBlockModal';
import { resolveCriminalRecordDisplay } from '@/utils/criminal-record-utils';
import { LEARNING360_PATH_STATUS_LABELS } from '@/components/tutors/tutor-configs';
import {
  getCriminalRecordDownloadUrl,
  getLearning360TutorPathStats,
} from '@/services/users.service';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link TutorDocumentsTab}. */
interface TutorDocumentsTabProps {
  record: TutorRecord;
  sections: Record<string, unknown>;
  matchedStudentsCount: number;
}

/** Document validation modal kind. */
type ModalType = 'validate' | 'refuse' | 'cancel' | 'activePairs' | null;

/** Download a URL as a named file via blob. */
async function downloadViaBlob(url: string, filename: string): Promise<void> {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank');
  }
}

/** Open a document URL in a new tab. */
function openDocumentPreview(url: string): void {
  try {
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win) window.open(url, '_blank');
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/** Preview / download row for a named document URL. */
function DocumentFileRow({ name, url }: Readonly<{ name?: string; url?: string }>) {
  if (!url && !name) return null;
  return (
    <div className="flex items-center justify-between gap-2 p-2 rounded-lg border border-border bg-card/50 overflow-hidden">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
        <span className="text-sm truncate text-foreground">{name ?? 'Document'}</span>
      </div>
      <div className="flex gap-1 shrink-0">
        {url && (
          <>
            <Button size="sm" variant="ghost" onClick={() => openDocumentPreview(url)}>
              <Eye className="w-3.5 h-3.5 mr-1" />
              Aperçu
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => downloadViaBlob(url, name ?? 'document')}
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              Télécharger
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/** Criminal-record row that fetches a signed URL before preview / download. */
function CriminalRecordDocumentRow({
  tutorUserId,
  name,
  staticUrl,
}: Readonly<{ tutorUserId: string; name?: string; staticUrl?: string }>) {
  const [loading, setLoading] = React.useState(false);

  const fetchSignedUrl = React.useCallback(async (): Promise<string | null> => {
    // Always fetch a fresh signed URL from the backend when tutorUserId is available.
    // Returning the stored staticUrl directly would serve outdated / legacy Firebase URLs
    // that point to old paths no longer in storage, causing 404s.
    try {
      setLoading(true);
      const url = await getCriminalRecordDownloadUrl(tutorUserId);
      if (url) return url;
      if (staticUrl && staticUrl !== 'stored') return staticUrl;
      return null;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, [tutorUserId, staticUrl]);

  const handlePreview = async () => {
    setLoading(true);
    try {
      const url = await fetchSignedUrl();
      if (!url) return;
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const pdfBlob = new Blob([blob], { type: blob.type || 'application/pdf' });
        const objectUrl = URL.createObjectURL(pdfBlob);
        const w = window.open(objectUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
        if (!w) window.open(url, '_blank', 'noopener,noreferrer');
      } catch {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    const url = await fetchSignedUrl();
    if (!url) return;
    await downloadViaBlob(url, name ?? 'casier-judiciaire.pdf');
  };

  return (
    <div className="flex items-center justify-between gap-2 p-2 rounded-lg border border-border bg-card/50 overflow-hidden">
      <div className="flex items-center gap-2 min-w-0">
        <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
        <span className="text-sm truncate text-foreground">{name ?? 'Document'}</span>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button size="sm" variant="ghost" onClick={handlePreview} disabled={loading}>
          {loading ? <Spinner size="sm" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
          Aperçu
        </Button>
        <Button size="sm" variant="ghost" onClick={handleDownload} disabled={loading}>
          <Download className="w-3.5 h-3.5 mr-1" />
          Télécharger
        </Button>
      </div>
    </div>
  );
}

/** Label/value row for a 360Learning stat. */
function Learning360StatLine({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm text-muted-foreground">
      <span>{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}

/** Fetched 360Learning path stats for one tutor. */
function Learning360PathStatsBlock({ userId }: Readonly<{ userId: string }>) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['learning360-path-stats', userId],
    queryFn: async () => {
      return getLearning360TutorPathStats(userId);
    },
    enabled: Boolean(userId),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Chargement des statistiques…</p>;
  }
  if (isError || !data || data.state === 'unavailable') {
    return (
      <p className="text-sm text-muted-foreground">Statistiques 360 Learning indisponibles.</p>
    );
  }
  if (data.state === 'not_started') {
    return <p className="text-sm text-muted-foreground">Formation non commencée.</p>;
  }

  return (
    <div className="mt-3 space-y-2 rounded-md bg-muted/40 px-3 py-2">
      <Learning360StatLine
        label="Avancement"
        value={data.progress == null ? 'Indisponible' : `${data.progress} %`}
      />
      <Learning360StatLine
        label="Temps passé"
        value={formatLearning360TimeSpent(data.timeSpentMs)}
      />
      <Learning360StatLine label="Démarrage" value={formatLearning360DateTime(data.startedAt)} />
      <Learning360StatLine label="Fin" value={formatLearning360DateTime(data.completedAt)} />
      <Learning360StatLine
        label="Statut"
        value={
          data.status
            ? (LEARNING360_PATH_STATUS_LABELS[data.status] ?? data.status)
            : 'Indisponible'
        }
      />
      <Learning360StatLine
        label="Score"
        value={data.score == null ? 'Indisponible' : `${data.score} %`}
      />
    </div>
  );
}

/** Tutor documents detail tab. */
export const TutorDocumentsTab: React.FC<TutorDocumentsTabProps> = ({
  record,
  sections,
  matchedStudentsCount,
}) => {
  const { user } = useAuthStore();
  const [openModal, setOpenModal] = React.useState<ModalType>(null);
  const { canValidateCriminalRecord } = usePageActions('tutors');

  const criminalRecordQuery = useGetTutorCriminalRecord(record.userId, user?.companyId ?? '', {
    enabled: !!user?.companyId,
  });
  const criminalRecordDecisionMutation = useUpdateCriminalRecordDecision();

  const handleDecision = (action: 'validate' | 'refuse' | 'cancel') => {
    if (!user?.companyId) return;
    criminalRecordDecisionMutation.mutate(
      { userId: record.userId, companyId: user.companyId, action },
      {
        onSuccess: () => setOpenModal(null),
        onError: () => setOpenModal(null),
      },
    );
  };

  function handleOpenCancel(): void {
    if (isTutorTutoringInProgress(record)) {
      setOpenModal('activePairs');
      return;
    }
    setOpenModal('cancel');
  }

  function handleCloseModal(): void {
    setOpenModal(null);
  }

  function renderValidatedCancelButton(): React.ReactNode {
    return (
      <Button
        size="sm"
        variant="outline"
        className="border-red-400 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600"
        onClick={handleOpenCancel}
      >
        <XCircle className="w-3.5 h-3.5 mr-1" />
        {matchedStudentsCount > 0 ? 'Annuler' : 'Refuser'}
      </Button>
    );
  }

  const tutorFullName = formatTutorDisplayName(record);

  const formatDateTime = (iso: string | null | undefined) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={FileCheck} title="Casier judiciaire" colorName="color-2" colSpan="full">
        {(() => {
          const officialDocs = sections['my_documents'] as any;
          const sectionCriminalRecord = officialDocs?.criminalRecord;
          const criminalRecord = criminalRecordQuery.data;

          const recordValidation = record.validation as any;
          const {
            status: effectiveStatus,
            validatedAt: effectiveValidatedAt,
            validatedBy: effectiveValidatedBy,
            rejectedAt: effectiveRejectedAt,
            rejectedBy: effectiveRejectedBy,
            hasPdf,
          } = resolveCriminalRecordDisplay({
            criminalRecord,
            sectionCriminalRecord,
            recordValidation,
          });

          return (
            <>
              {sectionCriminalRecord ? (
                <div className="space-y-3 mb-6">
                  <DetailField
                    label="Numéro"
                    value={
                      sectionCriminalRecord.recordNumber ?? sectionCriminalRecord.number ?? '—'
                    }
                  />
                  <DetailField
                    label="Délivré le"
                    value={formatDate(
                      sectionCriminalRecord.issueDate ?? sectionCriminalRecord.issuedAt,
                    )}
                  />
                  <DetailField label="Délivré par" value={sectionCriminalRecord.issuedBy} />
                  <div className="mt-3">
                    <CriminalRecordDocumentRow
                      tutorUserId={record.userId}
                      name={sectionCriminalRecord.documentName ?? sectionCriminalRecord.fileName}
                      staticUrl={sectionCriminalRecord.documentLink}
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground mb-6">
                  Aucun casier judiciaire déposé.
                </p>
              )}

              {sectionCriminalRecord && canValidateCriminalRecord && (
                <div className="relative rounded-xl border border-border overflow-hidden">
                  <LoadingOverlay
                    isLoading={criminalRecordDecisionMutation.isPending}
                    message="Mise à jour en cours..."
                  />

                  <div className="flex gap-4 p-4">
                    <div className="flex flex-col gap-2 min-w-[140px]">
                      {effectiveStatus === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            disabled={!hasPdf}
                            onClick={() => setOpenModal('validate')}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            Valider
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-400 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600"
                            disabled={!hasPdf}
                            onClick={() => setOpenModal('refuse')}
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1" />
                            Refuser
                          </Button>
                        </>
                      )}

                      {effectiveStatus === 'rejected' && (
                        <>
                          <Button
                            size="sm"
                            disabled={!hasPdf}
                            onClick={() => setOpenModal('validate')}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                            Valider
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setOpenModal('cancel')}
                          >
                            <Clock className="w-3.5 h-3.5 mr-1" />
                            Remettre en attente
                          </Button>
                        </>
                      )}

                      {effectiveStatus === 'validated' && renderValidatedCancelButton()}
                    </div>

                    <div className="flex-1">
                      {effectiveStatus === 'pending' && (
                        <div className="flex items-start gap-3 rounded-lg bg-orange-50 border border-orange-200 p-4 h-full">
                          <Clock className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-orange-700">
                              EN ATTENTE DE VALIDATION
                            </p>
                            <p className="text-xs text-orange-600 mt-1">
                              Veuillez examiner le casier judiciaire et choisir une action.
                            </p>
                            {!hasPdf && (
                              <p className="text-xs text-orange-500 mt-2 flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {getErrorMessage('criminalRecordPdfRequiredToDecide')}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {effectiveStatus === 'rejected' && (
                        <div className="flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 p-4 h-full">
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-red-700">CASIER REFUSÉ</p>
                            <p className="text-xs text-red-600 mt-1">
                              Refusé le {formatDateTime(effectiveRejectedAt)} par{' '}
                              {effectiveRejectedBy ?? '—'}
                            </p>
                          </div>
                        </div>
                      )}

                      {effectiveStatus === 'validated' && (
                        <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-4 h-full">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-green-700">CASIER VALIDÉ</p>
                            <p className="text-xs text-green-600 mt-1">
                              Validé le {formatDateTime(effectiveValidatedAt)} par{' '}
                              {effectiveValidatedBy ?? '—'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <ConfirmModal
                open={openModal === 'validate'}
                onClose={() => setOpenModal(null)}
                onConfirm={() => handleDecision('validate')}
                title="Valider le casier judiciaire"
                description={`Confirmer la validation du casier judiciaire de ${tutorFullName} ?`}
                confirmText="Valider"
                cancelText="Annuler"
                variant="validation"
                isLoading={criminalRecordDecisionMutation.isPending}
              />
              <ConfirmModal
                open={openModal === 'refuse'}
                onClose={() => setOpenModal(null)}
                onConfirm={() => handleDecision('refuse')}
                title="Refuser le casier judiciaire"
                description={`Confirmer le refus du casier judiciaire de ${tutorFullName} ?`}
                confirmText="Refuser"
                cancelText="Annuler"
                variant="danger"
                isLoading={criminalRecordDecisionMutation.isPending}
              />
              <ConfirmModal
                open={openModal === 'cancel'}
                onClose={() => setOpenModal(null)}
                onConfirm={() => handleDecision('cancel')}
                title={
                  criminalRecord?.status === 'rejected'
                    ? 'Annuler le refus'
                    : 'Annuler la validation'
                }
                description={`Remettre le casier judiciaire de ${tutorFullName} en attente ?`}
                confirmText="Confirmer"
                cancelText="Annuler"
                variant="danger"
                isLoading={criminalRecordDecisionMutation.isPending}
              />
            </>
          );
        })()}
      </DetailsCard>

      <div className="section-card-full grid grid-cols-2 gap-6">
        <DetailsCard icon={GraduationCap} title="Certification 360 Learning" colorName="color-3">
          {(() => {
            const stored = sections['my_documents'] as any;
            const legacy = sections['certification'] as any;
            const d =
              stored?.attestationNumber || stored?.obtentionDate || stored?.documentLink
                ? stored
                : legacy;
            return (
              <>
                {!d || (!d.attestationNumber && !d.obtentionDate && !d.documentLink) ? (
                  <p className="text-sm text-muted-foreground">Aucune certification renseignée.</p>
                ) : (
                  <div className="space-y-3">
                    <DetailField label="Numéro d'attestation" value={d.attestationNumber} />
                    <DetailField label="Date d'obtention" value={formatDate(d.obtentionDate)} />
                    {(d.documentName || d.documentLink) && (
                      <div className="mt-3">
                        <DocumentFileRow name={d.documentName} url={d.documentLink} />
                      </div>
                    )}
                  </div>
                )}
                <Learning360PathStatsBlock userId={record.userId} />
              </>
            );
          })()}
        </DetailsCard>

        <DetailsCard icon={FileText} title="Contrat" colorName="color-1">
          {(() => {
            const d = sections['contract'] as any;
            return (
              <>
                {d ? (
                  <div className="space-y-3">
                    <DetailField label="Signé le" value={formatDate(d.signedDate)} />
                    <DetailField label="Date d'upload" value={formatDate(d.uploadDate)} />
                    {(d.documentName || d.documentLink) && (
                      <div className="mt-3">
                        <DocumentFileRow name={d.documentName} url={d.documentLink} />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Aucun contrat renseigné.</p>
                )}
              </>
            );
          })()}
        </DetailsCard>
      </div>

      <ActivePairsBlockModal
        open={openModal === 'activePairs'}
        personUserId={record.userId}
        personName={tutorFullName}
        personKind="tutor"
        action="cancelCriminalRecord"
        onClose={handleCloseModal}
      />
    </div>
  );
};
